// ============================================================================
// ui/denomination-row.js — WHICH plates, once you have said how many
// ============================================================================
// Ryan, 2026-08-10: *"When you click the '+' for comp bumpers, the entire pill
// needs to condense towards the right… and to the left where the pill used to be,
// 5x circles need to appear, each with different weight denominations on them.
// From left to right include 55LB, 45LB, 35LB, 25LB, and 10LB."*
//
// ── THIS MAKES AN EXISTING BINDING VISIBLE; IT DOES NOT INVENT ONE ──────────
// A second configuration of a competition bumper has ALWAYS claimed a rung —
// `defaultWeightFor('bumper|Competition Urethane#2')` has answered 35 since the
// day configurations landed, and products.js calls that "a courtesy, not the
// enforcement". So one press of `+` really does mean 45 and 35, and every number
// on these circles is `configLadder`'s own order read back. What is new is that
// the buyer can now MOVE a claim, which is why `claimDenomination` had to make
// the rung a stored property of the configuration rather than a function of its
// instance number (see the block in ui/state.js for why not the number).
//
// ── ORDER: HEAVIEST FIRST, DELIBERATELY AGAINST THE OTHER SURFACE ───────────
// ⚠ `designLadder` sorts ASCENDING because Ryan asked the quote form for
// *"lowest to highest in terms of number"* (2026-08-02). This row is the
// opposite by his explicit instruction — "55LB, 45LB, 35LB, 25LB, and 10LB" —
// and it is sorted here rather than taken from `DESIGNABLE_WEIGHTS`' own order,
// which happens to descend today but is authored per product and would silently
// re-order this row the day somebody tidies the table.
//
// ── WHAT A TAP DOES, AND WHY IT IS NOT OBVIOUS ─────────────────────────────
// Tapping an unlit circle displaces a claim rather than adding one — the COUNT
// is the `±`'s job and the circles never change it. Which claim gets displaced
// is `claimDenomination`'s rule and Ryan's own words are quoted there.
// A LIT circle is inert; `−` is how you remove one. See the same function.
// ============================================================================

import { h, replaceChildren } from './dom.js';
import { productKey, designLadder, productSupportsMultiConfig } from '../products.js';
import { claimedDenominations, claimDenomination, productInstances } from './state.js';

/**
 * The rungs, heaviest first. Null when this product has nothing to choose between — a free-entry
 * barbell, or anything that cannot hold two configurations in the first place.
 * @returns {number[]|null}
 */
export function denominationRungs(key) {
  if (!productSupportsMultiConfig(key)) return null;
  const ladder = designLadder(key);
  if (!ladder || ladder.length < 2) return null;
  return [...ladder].sort((a, b) => b - a);
}

/**
 * @param {object} ctx  the app context (needs `.state`, optionally `.announce`)
 * @param {{category: string, kind: string, onChange?: () => void}} opts
 * @returns {{el: HTMLElement, sync: () => void, isOpen: () => boolean}} `sync` is called by the
 *   host's own repaint — like `createConfigAdder`, this schedules nothing of its own so it cannot
 *   fight a surface that rebuilds in place. `isOpen` answers "are there enough configurations for
 *   this row to be showing", which is the same question the host asks to condense its pill.
 */
export function createDenominationRow(ctx, { category, kind, onChange } = {}) {
  const key = productKey(category, kind);
  const rungs = denominationRungs(key);

  /* A product with no ladder gets a component that renders nothing, for the same reason
     `createConfigAdder` does: one hidden node beats three surfaces each remembering an `if`. */
  const el = h('div', { class: 'wd-den', hidden: true, role: 'group', 'aria-label': 'Plate weights' });
  if (!rungs) return { el, sync() {}, isOpen: () => false };

  const buttons = rungs.map((w) => {
    const btn = h('button', {
      type: 'button',
      class: 'wd-den__btn',
      'data-w': String(w),
      onClick: (e) => {
        /* ⛔ THE SAME GUARD `createConfigAdder` NEEDS, for the same reason: this row sits INSIDE
           `.wd-cfg-anchor`, whose other child is the pill — and on the surfaces where the pill's
           own row is a button, a bubbling click would toggle the product off underneath the tap. */
        e.stopPropagation();
        e.preventDefault();
        if (!claimDenomination(ctx.state, category, kind, w)) return;
        ctx.announce?.(`${fmt(w)} pounds selected.`);
        onChange?.();
        sync();
      },
    },
    /* Two lines, because the unit has to be readable at ~42px and "45LB" on one line either shrinks
       the number below legibility or forces an ellipsis. The number carries the weight (it is what
       is being compared); LB is a caption. `aria-label` says it properly for a screen reader. */
    h('span', { class: 'wd-den__n' }, fmt(w)),
    h('span', { class: 'wd-den__u' }, 'LB'));
    return btn;
  });
  replaceChildren(el, buttons);

  /** 2.5 → "2.5", 45 → "45". No trailing zeros on a face that has three characters of room. */
  function fmt(w) {
    return Number.isInteger(w) ? String(w) : String(Number(w.toFixed(1)));
  }

  /** Enough configurations for the row to mean anything — two, per Ryan's own collapse rule. */
  function isOpen() {
    return productInstances(ctx.state, category, kind).length >= 2;
  }

  function sync() {
    const open = isOpen();
    el.hidden = !open;
    /* ⭐ THE COMPONENT OWNS THE ANCHOR'S STATE CLASS, not its host. Two surfaces dress this state
       completely differently — the phone condenses its pill to a stub and puts the circles beside
       it, the desktop grows its pill downward and puts them underneath — but they are the SAME
       state, and it must flip on exactly the frames the row appears. A host computing it separately
       is a second place to disagree about when, which this file has watched go wrong before
       (config-adder's own note: "when two places need the same answer, one of them will be wrong").
       ⚠ `is-denoms`, not `is-condensed`: the pill condenses on one platform and expands on the
       other, so the class has to name the STATE rather than one platform's reaction to it. */
    el.closest('.wd-cfg-anchor')?.classList.toggle('is-denoms', open);
    const held = new Set(claimedDenominations(ctx.state, category, kind).map((d) => d.weight));
    buttons.forEach((btn, i) => {
      const w = rungs[i];
      const on = held.has(w);
      btn.classList.toggle('is-on', on);
      /* `aria-pressed`, not `disabled`: a lit circle is inert to a TAP but it is still the way the
         buyer reads which plates they have, so it must stay in the tab order and keep announcing
         its state. Disabling it would hide the answer from the people who most need it read out. */
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', `${fmt(w)} pound plate${on ? ', selected' : ''}`);
    });
  }

  el._syncDenominationRow = sync;
  sync();
  return { el, sync, isOpen };
}

/**
 * Repaint every denomination row under `root`, in place.
 *
 * The mirror of `syncConfigAdders`, and it exists for the same reason: the desktop chip surface
 * never rebuilds its rows (a rebuild flashes), so it repaints by walking the DOM it already has.
 * Asked of the DOM rather than of an array because those rows ARE rebuilt on every Back → Next
 * cycle, and a captured list would then be pointing at detached nodes.
 */
export function syncDenominationRows(root) {
  if (!root) return;
  root.querySelectorAll('.wd-den').forEach((n) => n._syncDenominationRow?.());
}
